import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cbtAPI } from '../../api/cbt';
import { toast } from 'react-toastify';
import { Sidebar } from './dashboard_component'; // 👈 added sidebar

const TestCompletion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailedResults, setDetailedResults] = useState([]);
  
  // Sidebar state
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };
  
  const sessionId = new URLSearchParams(location.search).get('session_id');
  
  useEffect(() => {
    fetchResults();
  }, [sessionId]);
  
  const fetchResults = async () => {
    try {
      setLoading(true);
      
      if (sessionId) {
        const response = await cbtAPI.getTestResults(sessionId);
        setResults(response.data);
        
        // If allowed, fetch detailed results
        if (response.data.cbt_data_id) {
          try {
            const detailedResponse = await cbtAPI.getCBTResults(response.data.cbt_data_id, {
              test_id: response.data.test_id
            });
            setDetailedResults(detailedResponse.data || []);
          } catch (error) {
            console.log('Detailed results not available');
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };
  
  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 80) return 'text-blue-600';
    if (percentage >= 70) return 'text-yellow-600';
    if (percentage >= 60) return 'text-orange-600';
    return 'text-red-600';
  };
  
  const getGradeEmoji = (percentage) => {
    if (percentage >= 90) return '🎉';
    if (percentage >= 80) return '👍';
    if (percentage >= 70) return '😊';
    if (percentage >= 60) return '🙂';
    return '😔';
  };
  
  const formatTimeTaken = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
        <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
        <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900">Processing Your Results...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!results) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
        <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
        <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <div className="flex items-center justify-center h-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
              <div className="text-gray-400 text-6xl mb-4">📝</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Results Pending</h3>
              <p className="text-gray-600 mb-6">Your test results are being processed. Please check back later.</p>
              <button
                onClick={() => navigate('/participate/dashboard')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const percentage = results.percentage || 0;
  const passed = results.passed || false;
  
  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block p-6 bg-white rounded-2xl shadow-lg mb-6">
              <div className={`text-6xl mb-4 ${getGradeColor(percentage)}`}>
                {getGradeEmoji(percentage)}
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {passed ? 'Congratulations!' : 'Test Completed'}
              </h1>
              <p className="text-lg text-gray-600">
                {passed 
                  ? 'You have successfully passed the test!' 
                  : 'You have completed the test.'
                }
              </p>
            </div>
          </div>
          
          {/* Results Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Score Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-blue-500">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                  <i className="fas fa-star text-blue-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Your Score</p>
                  <p className={`text-3xl font-bold ${getGradeColor(percentage)}`}>
                    {percentage.toFixed(1)}%
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${percentage >= 70 ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            {/* Grade Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-green-500">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                  <i className="fas fa-award text-green-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Grade</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {results.grade || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <i className={`fas fa-${passed ? 'check' : 'times'} mr-1`}></i>
                  {passed ? 'Passed' : 'Not Passed'}
                </span>
              </div>
            </div>
            
            {/* Time Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-purple-500">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                  <i className="fas fa-clock text-purple-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Time Taken</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatTimeTaken(results.time_taken)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Submitted Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-yellow-500">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4">
                  <i className="fas fa-calendar-check text-yellow-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Submitted</p>
                  <p className="text-lg font-bold text-gray-900">
                    {new Date(results.submitted_at).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(results.submitted_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Detailed Results (if available) */}
          {detailedResults.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-12">
              <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <i className="fas fa-chart-bar mr-3"></i>
                  Detailed Results
                </h2>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Question</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Your Answer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Correct Answer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {detailedResults.map((result, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">Q{index + 1}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {result.student_answer || 'Not answered'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {result.correct_answer || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span className={`font-medium ${
                              result.score_obtained > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {result.score_obtained || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              result.is_correct 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              <i className={`fas fa-${result.is_correct ? 'check' : 'times'} mr-1`}></i>
                              {result.is_correct ? 'Correct' : 'Incorrect'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          {/* Next Steps */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What's Next?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-home text-blue-600 text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Return to Dashboard</h3>
                <p className="text-sm text-gray-600">
                  Check your overall performance and track progress
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-chart-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">View Analytics</h3>
                <p className="text-sm text-gray-600">
                  Analyze your strengths and areas for improvement
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-certificate text-purple-600 text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Get Certificate</h3>
                <p className="text-sm text-gray-600">
                  Download your certificate if you passed the test
                </p>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="text-center space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/participate/dashboard')}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl font-bold hover:shadow-lg transition-all"
              >
                <i className="fas fa-tachometer-alt mr-3"></i>
                Go to Dashboard
              </button>
              
              <button
                onClick={() => navigate('/participate/test_results')}
                className="px-8 py-4 bg-white border-2 border-blue-600 text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition-all"
              >
                <i className="fas fa-file-alt mr-3"></i>
                View All Results
              </button>
            </div>
            
            <p className="text-gray-500 text-sm">
              Results are automatically saved to your profile
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCompletion;